import {
  Avatar,
  Badge,
  Divider,
  Icon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import {
  AuthContext,
  ConferenceContext,
  DiscussionContext,
  ThreadContext,
} from '@/store';
import UserAvatar from '@/components/ui/userAvatar';

const useStyles = (theme) => ({
  followMenu: {
    width: `300px !important`,
    maxHeight: `400px !important`,
    border: '1px solid #FFFFFF99',
    color: `${theme.palette.text?.primary || '#000000'} !important`,
    backgroundColor: `${
      theme.palette.background?.default || '#ffffff'
    } !important`,
    borderWidth: `1px !important`,
    borderStyle: 'solid !important',
    borderColor: `${
      theme.palette.background?.shadedown_20 || '#e0e0e0'
    } !important`,
    boxShadow: '0 0 15px rgb(0, 0, 0, 0.15) !important',
    padding: `5px !important`,
    borderRadius: `3px !important`,
  },
  tightMenuItem: {
    padding: '0 16px !important',
  },
});

const StyledBadge = (theme) => {
  const defaultBackground = `${
    theme?.palette?.background?.default || '#ffffff'
  }`;
  return {
    badge: {
      right: '1px !important',
      top: '30px !important',
      border: `1px solid ${defaultBackground} !important`,
      backgroundColor: `${defaultBackground} !important`,
      padding: '0 4px !important',
    },
  };
};

const NOTOFICATION_TEXTS = {
  'message-response': 'New forum response to',
  'message-suggest': 'New forum suggestion',
  'discussion-response': 'New forum response to',
  'discussion-suggest': 'New forum suggestion',
};

const ThreadMenu = (props, ref) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { authState } = useContext(AuthContext);
  const { threadState, loadThreads } = useContext(ThreadContext);
  const { openThreadPopup } = useContext(ConferenceContext);
  const {
    discussionNotifications,
    updateReadState,
    gotoDiscussion,
  } = useContext(DiscussionContext);

  const [menuState, setMenu] = useState({
    open: false,
    menuTarget: null,
    threads: [],
  });

  const handleClose = () => {
    setMenu({ open: false, menuTarget: null, threads: [] });
  };

  const getStortedThreads = () => {
    return [...threadState.threads].sort((a, b) =>
      a.threadVisitors.hasUnread && !b.threadVisitors.hasUnread
        ? -1
        : !a.threadVisitors.hasUnread && b.threadVisitors.hasUnread
        ? 1
        : 0
    );
  };

  useImperativeHandle(ref, () => ({
    open: (menuTarget) => {
      setMenu({ ...menuState, open: true, menuTarget: menuTarget });
      // Load list
      loadThreads();
      updateReadState();
    },
    close: handleClose,
  }));

  return (
    <Menu
      id="menu-followbar"
      anchorEl={menuState.menuTarget}
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted
      open={menuState.open}
      onClose={handleClose}
      slotProps={{ paper: { sx: classes.followMenu } }}
    >
      {threadState.threadsLoading || !menuState.open ? (
        <MenuItem>Loading...</MenuItem>
      ) : threadState.threads.length === 0 ? (
        <MenuItem>No private messages</MenuItem>
      ) : (
        [
          ...getStortedThreads(threadState.threads).map((thread) => {
            const txt = thread.lastThreadMessage
              ? thread.lastThreadMessage.text.substring(0, 15)
              : '';
            if (thread.visitors) {
              const visitors = thread.visitors.filter(
                (v) => v.id !== authState.user.id
              );
              const badgeContent =
                visitors.length < 2 ? 0 : `+${visitors.length - 1}`;
              return (
                <MenuItem
                  key={thread.id}
                  onClick={() => openThreadPopup(thread.id)}
                >
                  <ListItemIcon>
                    <Badge
                      badgeContent={badgeContent}
                      color="secondary"
                      sx={StyledBadge(theme)}
                    >
                      <Badge
                        invisible={!thread.threadVisitors.hasUnread}
                        variant="dot"
                        color="primary"
                      >
                        {visitors[0] ? (
                          <UserAvatar
                            size="medium"
                            user={
                              visitors.length === 1
                                ? visitors[0]
                                : thread.lastThreadMessage // Check the last message still exists
                                ? thread.lastThreadMessage.visitor
                                : null
                            }
                          />
                        ) : (
                          <Avatar />
                        )}
                      </Badge>
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      !visitors[0]
                        ? `User don't exist`
                        : txt.length === 15
                        ? `${txt}..`
                        : txt
                    }
                  />
                </MenuItem>
              );
            } else {
              return (
                <MenuItem
                  key={thread.id}
                  onClick={() => openThreadPopup(thread.id)}
                >
                  <ListItemIcon>
                    <Badge variant="dot" color="primary">
                      <Avatar />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={'New message...'} />
                </MenuItem>
              );
            }
          }),
        ]
      )}
      <Divider />
      {discussionNotifications.length > 0 ? (
        discussionNotifications.map((dn) => (
          <MenuItem
            key={dn.id}
            onClick={() => gotoDiscussion(dn.discussionId)}
            sx={classes.tightMenuItem}
          >
            <ListItemIcon>
              <Badge
                badgeContent={dn.count}
                color="secondary"
                sx={StyledBadge(theme)}
              >
                <Badge invisible={dn.viewed} variant="dot" color="primary">
                  <Icon>reply</Icon>
                </Badge>
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={NOTOFICATION_TEXTS[dn.type] || 'New response'}
              secondary={dn.text}
            />
          </MenuItem>
        ))
      ) : (
        <MenuItem>No forum responses</MenuItem>
      )}
    </Menu>
  );
};

export default forwardRef(ThreadMenu);
